// ============================================================================
// Auto-generated from backend spec (../api/docs/spec.md)
// Last synced: 2026-04-06
// Do not manually edit response types — re-run api-client-sync skill instead.
// ============================================================================

// --- Core entities ---

/** Course listing item returned by GET /courses */
export interface Course {
	id: string;
	title: string;
	description: string;
	language: "en" | "pt";
	price_cents: number;
	currency: string;
	module_count: number;
	total_duration_sec: number;
}

/** Full course entity as stored in DB (returned inside CourseContent) */
export interface CourseFull {
	id: string;
	title: string;
	description: string;
	language: "en" | "pt";
	price_cents: number;
	currency: string;
	stripe_price_id?: string;
	published: boolean;
	created_at: string;
	updated_at: string;
}

/** Course detail returned by GET /courses/:courseId (public, no video IDs) */
export interface CourseDetail {
	id: string;
	title: string;
	description: string;
	language: "en" | "pt";
	price_cents: number;
	currency: string;
	sections: SectionWithModules[];
}

/** Enrolled course content returned by GET /me/courses/:courseId */
export interface CourseContent {
	course: CourseFull;
	sections: SectionWithContent[];
}

// --- Section types ---

/** Section entity as stored in DB */
export interface Section {
	id: string;
	course_id: string;
	title: string;
	description: string;
	position: number;
	created_at: string;
	updated_at: string;
}

/** Section with module summaries (public course detail view) */
export interface SectionWithModules {
	id: string;
	title: string;
	description: string;
	position: number;
	modules: ModulePreview[];
}

/** Section with module progress (enrolled-user view) */
export interface SectionWithContent {
	id: string;
	title: string;
	description: string;
	position: number;
	modules: ModuleWithProgress[];
}

// --- Module types ---

/** Module as stored in DB (used for admin CRUD) */
export interface Module {
	id: string;
	section_id: string;
	title: string;
	description: string;
	position: number;
	video_id?: string;
	duration_sec: number;
	created_at: string;
	updated_at: string;
}

/** Module info visible to unenrolled users */
export interface ModulePreview {
	id: string;
	title: string;
	description: string;
	position: number;
	duration_sec: number;
	has_quiz: boolean;
}

/** Module with video URL and progress for enrolled users */
export interface ModuleWithProgress {
	id: string;
	title: string;
	description: string;
	position: number;
	video_url?: string;
	duration_sec: number;
	has_quiz: boolean;
	progress?: Progress;
}

/** Video/module progress for a user */
export interface Progress {
	id: string;
	user_id: string;
	module_id: string;
	status: "not_started" | "in_progress" | "completed";
	video_position: number;
	completed_at?: string;
	updated_at: string;
}

/** Student-facing enrollment with progress summary */
export interface Enrollment {
	id: string;
	course_id: string;
	course_title: string;
	status: "active" | "refunded" | "expired" | "disabled";
	enrolled_at: string;
	progress_percent: number;
	modules_completed: number;
	modules_total: number;
}

/** User entity */
export interface User {
	id: string;
	email: string;
	name: string;
	language: "en" | "pt";
	role: "student" | "admin";
	stripe_customer_id?: string;
	created_at: string;
	updated_at: string;
}

/** GET /me response */
export interface MeResponse {
	user: User;
	enrollment_count: number;
	completed_modules: number;
}

// --- Quiz types ---

/** Quiz returned by GET /me/quizzes/:moduleId (no correct answers) */
export interface Quiz {
	quiz_id: string;
	module_id: string;
	module_title: string;
	passing_score: number;
	questions: Question[];
	previous_attempts: number;
	best_score: number;
}

/** Quiz question (without correct answer, as served to students) */
export interface Question {
	id: string;
	type: "single_choice" | "multiple_choice";
	text: string;
	options: QuestionOption[];
}

/** Quiz question option */
export interface QuestionOption {
	id: string;
	text: string;
}

/** Quiz submission result returned by POST /me/quizzes/:moduleId/submit */
export interface QuizResult {
	score: number;
	passed: boolean;
	passing_score: number;
	results: QuestionResult[];
}

/** Individual question result with correct answer revealed */
export interface QuestionResult {
	question_id: string;
	correct: boolean;
	correct_answer: string | string[];
	explanation: string;
}

// --- Discount types ---

/** Stripe promotion code object returned by GET/POST /admin/discounts */
export interface Discount {
	id: string;
	code: string;
	active: boolean;
	type: "percentage" | "flat";
	percent_off?: number;
	amount_off?: number;
	currency?: string;
	max_redemptions?: number;
	times_redeemed: number;
	expires_at?: number;
	name?: string;
}

// --- Admin dashboard types ---

/** Per-course stats on the admin dashboard */
export interface DashboardCourseStat {
	id: string;
	title: string;
	enrollments: number;
	avg_progress_percent: number;
	completion_rate: number;
}

/** Recent enrollment shown on admin dashboard */
export interface RecentEnrollment {
	id: string;
	email: string;
	name: string;
	course_title: string;
	enrolled_at: string;
}

/** GET /admin/dashboard response */
export interface DashboardStats {
	total_students: number;
	active_enrollments: number;
	courses: DashboardCourseStat[];
	recent_enrollments: RecentEnrollment[];
	revenue_30d_cents: number;
}

// --- Admin student types ---

/** Student summary in admin list */
export interface StudentSummary {
	id: string;
	email: string;
	name: string;
	enrolled_courses: number;
	last_active: string;
}

/** Student enrollment detail (admin view) */
export interface StudentEnrollment {
	id: string;
	course_id: string;
	course_title: string;
	status: string;
	enrolled_by?: string;
	enrolled_at: string;
}

/** Student progress detail (admin view) */
export interface StudentProgress {
	module_id: string;
	module_title: string;
	course_id: string;
	status: string;
	video_position: number;
	completed_at?: string;
}

/** Student quiz history item (admin view) */
export interface QuizHistoryItem {
	quiz_id: string;
	module_id: string;
	module_title: string;
	score: number;
	passed: boolean;
	attempted_at: string;
}

/** GET /admin/students/:userId response */
export interface StudentDetail {
	user: User;
	enrollments: StudentEnrollment[];
	progress: StudentProgress[];
	quiz_history: QuizHistoryItem[];
}

// --- Checkout types ---

/** Checkout response returned by POST /checkout — a Stripe hosted checkout URL. */
export interface CheckoutResponse {
	checkout_url: string;
}

// --- Admin query params ---

/** Query parameters for GET /admin/students */
export interface AdminStudentsParams {
	search?: string;
	course_id?: string;
	limit?: number;
	offset?: number;
}

// --- Request bodies ---

/** POST /checkout request */
export interface CheckoutRequest {
	course_id: string;
	user_id: string;
	discount_code?: string;
}

/** PUT /me/progress/:moduleId request */
export interface UpdateProgressRequest {
	video_position: number;
	status: "not_started" | "in_progress" | "completed";
}

/** POST /me/quizzes/:moduleId/submit request */
export interface SubmitQuizRequest {
	answers: Array<{ question_id: string; answer: string | string[] }>;
}

/** POST /admin/enrollments request */
export interface CreateEnrollmentRequest {
	user_email: string;
	course_id: string;
	note?: string;
}

/** POST /admin/enrollments response */
export interface CreateEnrollmentResponse {
	id: string;
	user_id: string;
	course_id: string;
	status: string;
	enrolled_by: string;
	created_at: string;
}

/** PUT /admin/discounts/:discountId request */
export interface UpdateDiscountRequest {
	active?: boolean;
	name?: string;
}

/** POST /admin/discounts request */
export interface CreateDiscountRequest {
	code: string;
	type: "percentage" | "flat";
	amount: string;
	max_redemptions?: number;
	expires_at?: string;
	description?: string;
}

/** POST /admin/courses request */
export interface CreateCourseRequest {
	id: string;
	title: string;
	description?: string;
	language: "en" | "pt";
	price_cents: number;
	currency?: string;
	stripe_price_id?: string;
	published?: boolean;
}

/** POST /admin/courses response */
export interface CreateCourseResponse {
	id: string;
	title: string;
	description: string;
	language: string;
	price_cents: number;
	currency: string;
	published: boolean;
	created_at: string;
}

/** PUT /admin/courses/:courseId request (all fields optional) */
export interface UpdateCourseRequest {
	title?: string;
	description?: string;
	language?: string;
	price_cents?: number;
	currency?: string;
	stripe_price_id?: string;
	published?: boolean;
}

/** POST /admin/courses/:courseId/sections request */
export interface CreateSectionRequest {
	title: string;
	description?: string;
	position: number;
}

/** POST /admin/courses/:courseId/sections response */
export interface CreateSectionResponse {
	id: string;
	course_id: string;
	title: string;
	description: string;
	position: number;
	created_at: string;
}

/** PUT /admin/sections/:sectionId request (all fields optional) */
export interface UpdateSectionRequest {
	title?: string;
	description?: string;
	position?: number;
}

/** POST /admin/sections/:sectionId/modules request */
export interface CreateModuleRequest {
	title: string;
	description?: string;
	position: number;
	video_id?: string;
	duration_sec?: number;
}

/** POST /admin/sections/:sectionId/modules response */
export interface CreateModuleResponse {
	id: string;
	section_id: string;
	title: string;
	position: number;
	duration_sec: number;
	created_at: string;
}

/** PUT /admin/modules/:moduleId request (all fields optional) */
export interface UpdateModuleRequest {
	title?: string;
	description?: string;
	position?: number;
	section_id?: string;
	video_id?: string;
	duration_sec?: number;
}

/** PUT /admin/modules/:moduleId/quiz request */
export interface UpsertQuizRequest {
	questions: unknown[];
	passing_score?: number;
}

/** PUT /admin/modules/:moduleId/quiz response */
export interface UpsertQuizResponse {
	status: string;
	module_id: string;
}

// --- Generic responses ---

/** Status response from admin mutation endpoints */
export interface StatusResponse {
	status: string;
}

// --- Error ---

/** API error with status code, error code, and message */
export class ApiError extends Error {
	constructor(
		public status: number,
		public code: string,
		public override message: string,
	) {
		super(message);
		this.name = "ApiError";
	}
}

/** Thrown when the backend is unreachable (network failure, DNS, CORS, etc.) */
export class NetworkError extends Error {
	constructor(public cause?: unknown) {
		super("Network request failed");
		this.name = "NetworkError";
	}
}

/** POST /admin/students/:userId/enrollments/disable request and response */
export type DisableEnrollmentsRequest =
	| { course_id: string; all?: never }
	| { all: true; course_id?: never };
export interface DisableEnrollmentsResponse {
	disabled_count: number;
}

/** POST /admin/students/:userId/enrollments/enable request and response */
export interface EnableEnrollmentRequest {
	course_id: string;
}
export interface EnableEnrollmentResponse {
	enabled_count: number;
}
